import { PortfolioViewMode } from '@/constants/PortfolioViewMode';
import { createGlobalState } from '@vueuse/shared';
import type { ComputedRef, Ref } from 'vue';
import { computed, ref, watchEffect } from 'vue';
import type { IPortfolioTree, IPortfolioTreeDraftRequestBody } from '@/types/IPortfolioTree';
import { LocalStorageEntities } from '@/constants/LocalStorageEntities';
import { NavigationPanelGroupingOptionType } from '@/constants/NavigationPanelGroupingOption';
import type { PortfolioTreeGroupingDTO } from '@/types/portfolio/PortfolioTreeGroupingDTO';
import { useLocalStorage } from '@vueuse/core';
import { getDefaultViewGroupingConfig } from '@/utils/defaultViewGroupingConfig';
import { useIndexUniqueIdentifier } from './useCorrectIdentifier';
import { useItemUnderAnalysisGlobalState } from './useItemUnderAnalysis';

const usePortfolioViewModeGlobalState = createGlobalState((): Ref<PortfolioViewMode> => {
  return ref<PortfolioViewMode>(PortfolioViewMode.DEFAULT);
});

const usePortfolioViewGroupingConfigStorageGlobalState = createGlobalState(
  (): Ref<Record<string, PortfolioTreeGroupingDTO>> => {
    return useLocalStorage<Record<string, PortfolioTreeGroupingDTO>>(
      LocalStorageEntities.PORTFOLIO_VIEW_GROUPING_CONFIG,
      {},
    );
  },
);

/**
 * This function checks whether the returned draft currently has the view mode applied.
 *
 * VIEW capability now comes from persisted local storage, not from reading
 * `groupingConfig[PortfolioViewMode.VIEW]` off the draft itself.
 */
export const getAppliedPortfolioViewMode = (tree: IPortfolioTree | null): PortfolioViewMode => {
  return tree?.portfolioTree.mode ?? PortfolioViewMode.DEFAULT;
};

export const usePortfolioViewMode = (): {
  portfolioViewModeOverride: Ref<PortfolioViewMode>;
} => {
  /**
   * Reactive reference to the current portfolio mode
   */
  const portfolioViewModeOverride = usePortfolioViewModeGlobalState();

  return {
    portfolioViewModeOverride,
  };
};

export const usePortfolioViewGroupingConfig = ({
  indexUniqueIdentifier,
}: {
  indexUniqueIdentifier: ComputedRef<string | undefined>;
}): {
  /**
   * Saves the grouping config to the local storage.
   * Removes the config if the grouping config is null.
   */
  saveViewGroupingConfig: ({ groupingConfig }: { groupingConfig: PortfolioTreeGroupingDTO | null | undefined }) => void;

  /**
   * Saved VIEW grouping for this identifier, or the type-specific default when the
   * ungrouped tree exceeds the pin cap (peer groups have no default).
   */
  viewGroupingConfig: Ref<PortfolioTreeGroupingDTO | undefined>;
  /**
   * True when a saved or default VIEW grouping is in effect.
   */
  hasViewGroupingConfig: Ref<boolean>;
  /**
   * Returns the request body for the portfolio view draft.
   * The PortfolioViewMode is kept for backwards compatibility with the old logic.
   */
  viewDraftRequestBody: Ref<IPortfolioTreeDraftRequestBody | undefined>;
} => {
  const portfolioViewGroupingConfigStorage = usePortfolioViewGroupingConfigStorageGlobalState();

  const saveViewGroupingConfig = ({
    groupingConfig,
  }: {
    groupingConfig: PortfolioTreeGroupingDTO | null | undefined;
  }): void => {
    if (!indexUniqueIdentifier.value) return;

    if (!groupingConfig) {
      delete portfolioViewGroupingConfigStorage.value[indexUniqueIdentifier.value];
      portfolioViewGroupingConfigStorage.value = {
        ...portfolioViewGroupingConfigStorage.value,
      };
      return;
    }

    portfolioViewGroupingConfigStorage.value = {
      ...portfolioViewGroupingConfigStorage.value,
      [indexUniqueIdentifier.value]: groupingConfig,
    };
  };

  const { masterPortfolioTreeTempRef } = useItemUnderAnalysisGlobalState();

  const viewGroupingConfig = computed((): PortfolioTreeGroupingDTO | undefined => {
    if (!indexUniqueIdentifier.value) return undefined;

    const savedConfig = portfolioViewGroupingConfigStorage.value[indexUniqueIdentifier.value];
    if (savedConfig) return savedConfig;

    if (!masterPortfolioTreeTempRef.value) return undefined;

    const isCurrentPortfolio =
      masterPortfolioTreeTempRef.value != null &&
      [
        masterPortfolioTreeTempRef.value.originalSlug,
        masterPortfolioTreeTempRef.value.portfolioTree.reference,
      ].includes(indexUniqueIdentifier.value);

    if (!isCurrentPortfolio) {
      return undefined;
    }

    return getDefaultViewGroupingConfig({
      type: masterPortfolioTreeTempRef.value.portfolioTree.type,
      itemCount: masterPortfolioTreeTempRef.value.portfolioTree.components.length,
    });
  });

  const hasViewGroupingConfig = computed(
    (): boolean =>
      viewGroupingConfig.value != null && viewGroupingConfig.value.type !== NavigationPanelGroupingOptionType.HIERARCHY,
  );

  const viewDraftRequestBody = computed(() => {
    if (!viewGroupingConfig.value || viewGroupingConfig.value.type === NavigationPanelGroupingOptionType.HIERARCHY) {
      return undefined;
    }

    return {
      groupingConfig: {
        [PortfolioViewMode.VIEW]: viewGroupingConfig.value,
      },
    };
  });

  return {
    viewGroupingConfig,
    hasViewGroupingConfig,
    viewDraftRequestBody,
    saveViewGroupingConfig,
  };
};

/**
 * Visible draft mode is derived from the panel + saved grouping only:
 * - collapsed panel + saved view grouping => VIEW
 * - otherwise => DEFAULT
 */
export const getVisiblePortfolioViewMode = ({
  isNavigationPanelExpanded,
  hasViewGroupingConfig,
}: {
  isNavigationPanelExpanded: boolean;
  hasViewGroupingConfig: boolean;
}): PortfolioViewMode =>
  !isNavigationPanelExpanded && hasViewGroupingConfig ? PortfolioViewMode.VIEW : PortfolioViewMode.DEFAULT;

/**
 * Keeps the visible draft mode in sync with the navigation panel and saved grouping.
 * DataLayer calls this while the draft page is mounted.
 */
export const useNavigationPanelViewModeSync = ({
  isNavigationPanelExpanded,
  hasViewGroupingConfig,
}: {
  isNavigationPanelExpanded: Ref<boolean>;
  hasViewGroupingConfig: Ref<boolean>;
}): void => {
  const { portfolioViewModeOverride } = usePortfolioViewMode();

  watchEffect(() => {
    const nextMode = getVisiblePortfolioViewMode({
      isNavigationPanelExpanded: isNavigationPanelExpanded.value,
      hasViewGroupingConfig: hasViewGroupingConfig.value,
    });

    if (portfolioViewModeOverride.value !== nextMode) {
      portfolioViewModeOverride.value = nextMode;
    }
  });
};

const useDraftSlugViewGroupingCacheKeyGlobalState = createGlobalState((): Ref<string | undefined> => {
  const indexUniqueIdentifier = useIndexUniqueIdentifier();
  const { portfolioViewModeOverride } = usePortfolioViewMode();
  const { viewGroupingConfig } = usePortfolioViewGroupingConfig({
    indexUniqueIdentifier,
  });
  return computed((): string | undefined => {
    if (portfolioViewModeOverride.value !== PortfolioViewMode.VIEW) {
      return undefined;
    }

    return JSON.stringify(viewGroupingConfig.value ?? null);
  });
});

/**
 * Cache key fragment used by draftSlug queries so VIEW grouping changes create
 * a distinct cache identity and trigger invalidation
 */
export const useDraftSlugViewGroupingCacheKey = (): {
  viewGroupingCacheKey: Ref<string | undefined>;
} => {
  const viewGroupingCacheKey = useDraftSlugViewGroupingCacheKeyGlobalState();

  return {
    viewGroupingCacheKey,
  };
};
